import { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import {
  Search,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  RotateCcw,
  RefreshCcw,
  XCircle,
  History,
  Check,
  Ban,
  UserCog,
  X,
  Inbox,
  CalendarPlus,
} from 'lucide-react'
import {
  adminApi,
  type AdminInterviewerOption,
  type AdminRescheduleHistoryItem,
  type AdminChangeRequestItem,
  type AvailabilityExtensionItem,
} from '../../lib/api/admin'
import {
  formatDateInTz,
  formatTimeInTz,
  tzAbbr,
  utcIsoToLocalInput,
} from '../../lib/utils/timezone'
import { schedulerApi, type AvailabilitySlot } from '../../lib/api/scheduler'
import { friendlyApiError } from '../../lib/utils/apiError'
import { buildAdminIcs, downloadIcs } from '../../lib/utils/ics'
import ExportMenu, { type ExportOptions } from '../../components/ExportMenu'
import { useSessionExport } from '../../hooks/useSessionExport'
import AdminManageAvailability from './AdminManageAvailability'
import type { ExportFormat, SessionExportPayload } from '../../lib/api/admin'

interface SessionCounts {
  upcoming: number
  completed: number
  pending_evaluation: number
  rescheduled: number
  cancelled: number
  // Includes past sessions with no confirmed attendance — the server derives
  // those as no-shows now, so there is no separate "unconfirmed" bucket.
  no_show?: number
  total: number
}

interface SessionItem {
  id: number
  source: string
  client_id: number | null
  client_name: string | null
  client_email: string | null
  interviewer_id: number | null
  interviewer_alias: string | null
  scheduled_at: string
  duration_minutes: number
  display_status: string
  // True when display_status came from ATTENDANCE on a row whose stored status
  // is still 'confirmed' — i.e. the server decided "no-show" or "cancelled"
  // because of who joined, not because a human said so. Such a row can still be
  // resolved; a genuinely cancelled booking cannot.
  is_derived_attendance?: boolean
  amount: number | null
  no_show_party?: string | null
  zoom_join_url: string | null
  zoom_password: string | null
  calendar_uid?: string | null
  calendar_sequence?: number
}

type FilterTab =
  | 'all'
  | 'upcoming'
  | 'completed'
  | 'pending_evaluation'
  | 'rescheduled'
  | 'cancelled'
  | 'no_show'

const STATUS_TABS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'All' },
  // "Upcoming" = every session still to come, including rescheduled ones (a
  // rescheduled session is still upcoming). Backend maps status=upcoming to
  // confirmed/scheduled future bookings, so the row count matches the badge.
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'completed', label: 'Completed' },
  { key: 'pending_evaluation', label: 'Pending Evaluations' },
  { key: 'rescheduled', label: 'Rescheduled' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'no_show', label: 'No-show' },
]

// Largest extension an admin may grant, mirroring the backend rule
// MAX_TOTAL_WINDOW_DAYS (60) - MAX_BASE_WINDOW_DAYS (45). The server is still
// authoritative and re-derives the real per-interviewer cap (60 - their base);
// this only stops the form submitting a value that would be rejected.
const MAX_EXTENSION_DAYS = 15

// Booking states where reschedule/reassign/cancel/mark-status make sense.
const ACTIONABLE_STATUSES = ['scheduled', 'rescheduled']

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'completed': return 'badge-success'
    case 'scheduled': return 'badge-primary'
    case 'pending_evaluation': return 'badge-warning'
    case 'rescheduled': return 'badge-warning'
    case 'cancelled': return 'badge-danger'
    case 'no_show': return 'badge-danger'
    default: return 'badge-primary'
  }
}

const formatStatus = (status: string) =>
  status === 'no_show'
    ? 'No-show'
    : status.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')

const timeAgo = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

type ModalKind = 'reschedule' | 'reassign' | 'cancel' | 'complete' | 'history'
type ModalState = { kind: ModalKind; session: SessionItem } | null

/** The change request the session list is currently pinned to, if any. */
type FocusedRequest = { bookingId: number; req: AdminChangeRequestItem }

// An APPROVED request is not finished — the admin still has to carry the change
// out on the session row, and only that closes it. Both states therefore mean
// "you still have something to do", which is why the queue loads status=open.
const isAwaitingAction = (r: AdminChangeRequestItem) => r.status === 'approved'

/**
 * A CANCEL request is never approved as a separate step.
 *
 * Approving it recorded a decision and changed nothing, so the admin still had to
 * go and cancel the session — two clicks to express one intention, and a status
 * that told them what they already knew. Cancelling the session is the decision,
 * and it closes the request. (Rows already sitting at `approved` from before this
 * are still rendered; only the button is gone.)
 */
const needsApprovalStep = (r: AdminChangeRequestItem) => r.type !== 'cancel'

const crStatusLabel = (r: AdminChangeRequestItem): string =>
  isAwaitingAction(r)
    ? r.type === 'cancel'
      ? 'Approved — cancel the session'
      : 'Approved — reschedule the session'
    : r.type === 'cancel'
      ? 'Awaiting cancellation'
      : 'Pending review'

const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 30,
  padding: 0,
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'white',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  transition: 'all 0.15s',
}

export default function AdminSessions() {
  const [counts, setCounts] = useState<SessionCounts>({
    upcoming: 0, completed: 0, pending_evaluation: 0, rescheduled: 0, cancelled: 0, total: 0,
  })
  const [sessions, setSessions] = useState<SessionItem[]>([])
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<FilterTab>('all')
  const [fetchError, setFetchError] = useState('')
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [timezone, setTimezone] = useState('UTC')

  // Interviewer filter
  const [interviewers, setInterviewers] = useState<AdminInterviewerOption[]>([])
  const [interviewerFilter, setInterviewerFilter] = useState('') // '' = all

  // Export (CSV / Excel / PDF, inline or background) — see useSessionExport.
  const { runExport, exporting } = useSessionExport()

  // View: session list vs change-request review queue vs extension review queue
  const [view, setView] = useState<'sessions' | 'change_requests' | 'extended_availability' | 'manage_availability'>('sessions')
  const [changeRequests, setChangeRequests] = useState<AdminChangeRequestItem[]>([])
  const [crLoading, setCrLoading] = useState(false)
  // Clicking a change request pins the session list to that one booking, so the
  // admin acts on the right session instead of hunting for it by name and time.
  const [focused, setFocused] = useState<FocusedRequest | null>(null)

  // Availability-extension review queue
  const [extRequests, setExtRequests] = useState<AvailabilityExtensionItem[]>([])
  const [extLoading, setExtLoading] = useState(false)
  // The queue used to load ONLY pending rows, which meant an APPROVED
  // extension vanished from the table and its Revoke button became
  // unreachable — an admin had no way to take back a granted window.
  const [extFilter, setExtFilter] = useState<'pending' | 'approved' | 'all'>('pending')
  // Badge must keep meaning "needs review", so it is always the pending count,
  // independent of which filter the table is showing.
  const [extPendingCount, setExtPendingCount] = useState(0)
  // Resolve modal: approve/reject/revoke an existing request. The admin-initiated
  // direct-grant path was removed — an extension now always starts as an
  // interviewer request. The 'decide' discriminant is kept so the mode is
  // explicit if another mode is ever added back.
  const [extModal, setExtModal] = useState<
    | { mode: 'decide'; req: AvailabilityExtensionItem; action: 'approve' | 'reject' | 'revoke' }
    | null
  >(null)
  const [extNote, setExtNote] = useState('')
  const [extTrimDays, setExtTrimDays] = useState('') // optional smaller grant on approve

  // Booking-action modal
  const [modal, setModal] = useState<ModalState>(null)
  const [reason, setReason] = useState('')
  const [reassignId, setReassignId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [history, setHistory] = useState<AdminRescheduleHistoryItem[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  // Slot picker (reschedule + reassign). Admins may ONLY pick an interviewer's
  // real open slot — never an arbitrary time. Slots come from the same native
  // availability endpoint the client booking flow uses, so the offered times
  // always match what the server will accept (_assert_native_slot_open).
  const todayStr = new Date().toISOString().slice(0, 10)
  const [slotDate, setSlotDate] = useState(todayStr)
  const [slots, setSlots] = useState<AvailabilitySlot[]>([])
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [slotsError, setSlotsError] = useState('')
  const [selectedSlotUtc, setSelectedSlotUtc] = useState('')

  // Change-request resolve modal
  const [crModal, setCrModal] = useState<{ req: AdminChangeRequestItem; action: 'approve' | 'reject' } | null>(null)
  const [crNotes, setCrNotes] = useState('')

  const fetchAll = useCallback(
    async (
      tab: FilterTab,
      searchVal: string,
      pg: number,
      interviewerId: string,
      focusBookingId?: number | null,
    ) => {
      setLoading(true)
      setFetchError('')
      try {
        const params: Record<string, unknown> = { page: pg, per_page: 20 }
        // A focused lookup asks for ONE booking by id and deliberately ignores the
        // tab/search/interviewer filters: those would hide the very session the
        // admin just clicked through to (a cancelled one under "Upcoming", say).
        if (focusBookingId != null) {
          params.booking_id = focusBookingId
        } else {
          if (tab !== 'all') params.status = tab
          if (searchVal.trim()) params.search = searchVal.trim()
          if (interviewerId) params.interviewer_id = interviewerId
        }

        // The badges describe the rows on screen, so they take the SAME
        // interviewer and search filters as the list (never the tab — the point
        // of the badges is to show what the OTHER tabs hold). Under a focused
        // lookup the list ignores the filters, so the badges stay unfiltered too.
        const countParams: Record<string, unknown> = {}
        if (focusBookingId == null) {
          if (searchVal.trim()) countParams.search = searchVal.trim()
          if (interviewerId) countParams.interviewer_id = interviewerId
        }

        const [countsData, sessionsData] = await Promise.all([
          adminApi.getSessionCounts(countParams),
          adminApi.getSessions(params),
        ])
        setCounts(countsData)
        const list = sessionsData.data || sessionsData || []
        setSessions(Array.isArray(list) ? list : [])
        setTotal(sessionsData.total ?? (Array.isArray(list) ? list.length : 0))
        setTotalPages(sessionsData.last_page ?? 1)
      } catch (err) {
        setFetchError(friendlyApiError(err, 'Failed to load sessions.'))
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  // "open" = pending + approved. Loading only `pending` used to make an approved
  // request disappear the moment it was approved, even though the cancel or
  // reschedule it authorised had not been carried out yet — so the queue looked
  // empty while work was still outstanding. It now stays listed until the action
  // is actually performed on the session.
  const loadChangeRequests = useCallback(async () => {
    setCrLoading(true)
    try {
      setChangeRequests(await adminApi.getChangeRequests({ status: 'open' }))
    } catch {
      // Non-fatal: leave the badge/queue as-is.
    } finally {
      setCrLoading(false)
    }
  }, [])

  const loadExtRequests = useCallback(async (filter: 'pending' | 'approved' | 'all') => {
    setExtLoading(true)
    try {
      const rows = await adminApi.listAvailabilityExtensions(
        filter === 'all' ? undefined : { status: filter },
      )
      setExtRequests(rows)
      if (filter === 'pending' || filter === 'all') {
        // The rows already contain every pending request.
        setExtPendingCount(rows.filter((r) => r.status === 'pending').length)
      } else {
        // Viewing approved only — fetch the pending count so the tab badge does
        // not go stale while the admin is filtering.
        const pending = await adminApi.listAvailabilityExtensions({ status: 'pending' })
        setExtPendingCount(pending.length)
      }
    } catch {
      // Non-fatal: leave the badge/queue as-is.
    } finally {
      setExtLoading(false)
    }
  }, [])

  useEffect(() => {
    adminApi.getPublicSettings().then((s) => {
      if (s?.timezone) setTimezone(s.timezone)
    }).catch(() => {})
    adminApi.getSessionInterviewers().then(setInterviewers).catch(() => {})
    loadChangeRequests()
    loadExtRequests(extFilter)
  }, [loadChangeRequests, loadExtRequests]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAll(activeTab, search, page, interviewerFilter, focused?.bookingId)
  }, [activeTab, page, interviewerFilter, focused]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1)
      fetchAll(activeTab, search, 1, interviewerFilter, focused?.bookingId)
    }, 350)
    return () => clearTimeout(timer)
  }, [search]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (tab: FilterTab) => {
    setView('sessions')
    // Choosing a tab is an explicit "show me the whole list again", so it drops
    // the single-session pin rather than silently ignoring the click.
    setFocused(null)
    setActiveTab(tab)
    setPage(1)
  }

  /** Jump from the change-request queue to the exact session it refers to. */
  const openSessionForRequest = (req: AdminChangeRequestItem) => {
    setFocused({ bookingId: req.booking_id, req })
    setView('sessions')
    setActiveTab('all')
    setPage(1)
    setSearch('')
    setInterviewerFilter('')
  }

  const clearFocus = () => {
    setFocused(null)
    setPage(1)
  }

  const refetch = () => {
    fetchAll(activeTab, search, page, interviewerFilter, focused?.bookingId)
    loadChangeRequests()
  }

  /**
   * Finish a booking action that was reached from the change-request queue.
   *
   * The action itself is what closes the request (see AdminService's
   * `_close_change_requests_for`), so this unpins the session and hands the admin
   * back to the queue — where the row they were working is now gone. Outside the
   * focused flow it is a plain refetch.
   */
  const finishFocusedAction = () => {
    if (focused) {
      setFocused(null)
      setView('change_requests')
    }
    loadChangeRequests()
    fetchAll(activeTab, search, page, interviewerFilter, null)
  }

  // Export mirrors exactly what is on screen — the active tab, the search box
  // and the interviewer filter — so "what you see is what you get". The hook
  // handles the rest: a small result set downloads immediately, a large one is
  // generated in the background and downloads when ready.
  const buildExportPayload = (
    format: ExportFormat,
    options: ExportOptions,
  ): SessionExportPayload => ({
    format,
    ...(activeTab !== 'all' ? { status: activeTab } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(interviewerFilter ? { interviewer_id: Number(interviewerFilter) } : {}),
    timezone: options.timezone,
    ...(options.columns ? { columns: options.columns } : {}),
  })

  // What the counts currently describe — '' when they cover the whole platform.
  // Only the interviewer filter is named: it applies the moment it is picked,
  // whereas the search box is not applied until it is submitted, so echoing
  // half-typed text here would describe numbers that have not been fetched yet.
  const countsScope = (() => {
    if (focused || !interviewerFilter) return ''
    const iv = interviewers.find((i) => String(i.interviewer_id) === interviewerFilter)
    return iv ? iv.alias || `Interviewer #${iv.interviewer_id}` : ''
  })()

  const getTabCount = (key: FilterTab): number => {
    if (key === 'all') return counts.total
    if (key === 'upcoming') return counts.upcoming
    if (key === 'pending_evaluation') return counts.pending_evaluation
    if (key === 'rescheduled') return counts.rescheduled
    if (key === 'no_show') return counts.no_show ?? 0
    return (counts[key as keyof SessionCounts] as number) ?? 0
  }

  // ── Booking actions ─────────────────────────────────────────────────────────
  const isBooking = (s: SessionItem) => s.source === 'booking'
  const canModify = (s: SessionItem) => isBooking(s) && ACTIONABLE_STATUSES.includes(s.display_status)
  // Once a session reads Cancelled, nobody changes its status — a cancellation is
  // final, so the row is read-only however it got there. That includes the rows
  // filed under Cancelled because no attendance was ever recorded: they used to
  // keep "Mark completed" for the case where the session ran and Zoom never
  // reported it, and that correction is no longer offered here.
  const canResolveAttendance = (s: SessionItem) => canModify(s)
  // A calendar file is only useful for a session that is still going to happen.
  // Cancelled and completed rows have nothing left to put in a calendar.
  const canAddToCalendar = (s: SessionItem) => canModify(s) && !!s.zoom_join_url

  const loadHistory = async (bookingId: number) => {
    setHistoryLoading(true)
    setHistory([])
    try {
      setHistory(await adminApi.getSessionHistory(bookingId))
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to load history.'))
    } finally {
      setHistoryLoading(false)
    }
  }

  const loadSlots = async (interviewerId: number, date: string) => {
    setSlotsLoading(true)
    setSlotsError('')
    setSlots([])
    setSelectedSlotUtc('')
    try {
      const res = await schedulerApi.getAvailability(interviewerId, date, timezone)
      // Defensive: the endpoint already returns only open slots; drop any booked.
      setSlots((res.slots || []).filter((s) => !s.is_booked))
    } catch (err) {
      setSlotsError(friendlyApiError(err, 'Failed to load available slots.'))
    } finally {
      setSlotsLoading(false)
    }
  }

  const renderSlotList = () => {
    if (slotsLoading) return <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>Loading available slots…</p>
    if (slotsError) return <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>{slotsError}</p>
    if (slots.length === 0) return <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>No available slots on this day. Try another date.</p>
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 180, overflowY: 'auto' }}>
        {slots.map((s) => {
          const sel = s.start_utc === selectedSlotUtc
          return (
            <button
              key={s.start_utc}
              type="button"
              onClick={() => setSelectedSlotUtc(s.start_utc)}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: 13,
                cursor: 'pointer',
                border: sel ? '1px solid var(--primary-navy)' : '1px solid var(--border)',
                background: sel ? 'var(--primary-navy)' : 'white',
                color: sel ? 'white' : 'var(--text)',
                transition: 'all 0.15s',
              }}
            >
              {formatTimeInTz(s.start_utc, timezone)}
            </button>
          )
        })}
      </div>
    )
  }

  const openModal = (kind: ModalKind, session: SessionItem) => {
    setModal({ kind, session })
    setReason('')
    setReassignId('')
    setSlots([])
    setSlotsError('')
    setSelectedSlotUtc('')
    // Default the slot-picker date to the booking's current day (never in the past).
    const currentDate = utcIsoToLocalInput(session.scheduled_at, timezone).slice(0, 10)
    const startDate = currentDate < todayStr ? todayStr : currentDate
    setSlotDate(startDate)
    if (kind === 'reschedule' && session.interviewer_id != null) {
      loadSlots(session.interviewer_id, startDate)
    }
    if (kind === 'history') loadHistory(session.id)
  }

  const closeModal = () => {
    if (submitting) return
    setModal(null)
    setHistory([])
  }

  const handleReschedule = async () => {
    if (!modal || !selectedSlotUtc) return
    setSubmitting(true)
    try {
      await adminApi.rescheduleBooking(modal.session.id, {
        new_scheduled_at: selectedSlotUtc,
        reason: reason.trim() || undefined,
      })
      toast.success(
        focused
          ? 'Session rescheduled — the change request is now resolved and off the queue.'
          : 'Session rescheduled.',
      )
      setModal(null)
      finishFocusedAction()
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to reschedule session.'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleReassign = async () => {
    if (!modal || !reassignId || !selectedSlotUtc) return
    setSubmitting(true)
    try {
      await adminApi.reassignBooking(modal.session.id, {
        new_interviewer_id: Number(reassignId),
        new_scheduled_at: selectedSlotUtc,
        reason: reason.trim() || undefined,
      })
      toast.success(
        focused
          ? 'Session reassigned — the change request is now resolved and off the queue.'
          : 'Session reassigned.',
      )
      setModal(null)
      finishFocusedAction()
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to reassign session.'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = async () => {
    if (!modal) return
    setSubmitting(true)
    try {
      await adminApi.cancelBooking(modal.session.id, { reason: reason.trim() || undefined })
      toast.success(
        focused
          ? 'Session cancelled — the change request is now resolved and off the queue.'
          : 'Session cancelled.',
      )
      setModal(null)
      // Cancelling closes every open request on this booking server-side, so the
      // admin is returned to the queue to see it gone rather than left staring at
      // a pinned session that no longer needs anything.
      finishFocusedAction()
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to cancel session.'))
    } finally {
      setSubmitting(false)
    }
  }

  // Completed only — marking a no-show is not an admin action (attendance is
  // decided from Zoom join data and the grace job).
  const handleStatus = async (status: 'completed') => {
    if (!modal) return
    setSubmitting(true)
    try {
      await adminApi.setBookingStatus(modal.session.id, status)
      // Settling the outcome closes any open request on the session server-side —
      // the only way to clear a request whose session has already run.
      toast.success(
        focused
          ? 'Marked as completed — the change request is now resolved and off the queue.'
          : 'Marked as completed.',
      )
      setModal(null)
      finishFocusedAction()
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to update status.'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleResolve = async () => {
    if (!crModal) return
    setSubmitting(true)
    try {
      await adminApi.resolveChangeRequest(crModal.req.id, {
        action: crModal.action,
        admin_notes: crNotes.trim() || undefined,
      })
      const { req, action } = crModal
      setCrModal(null)
      setCrNotes('')
      if (action === 'approve') {
        // Approving is a decision, not the action. Take the admin straight to the
        // session so the step that actually matters is one click away instead of
        // something they have to remember to come back and do.
        toast.success(
          req.type === 'cancel'
            ? 'Approved. The session is NOT cancelled yet — cancel it below to complete the request.'
            : 'Approved. Reschedule the session below to complete the request.',
        )
        await loadChangeRequests()
        openSessionForRequest(req)
      } else {
        toast.success('Request rejected.')
        refetch()
      }
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to resolve request.'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleResolveExt = async () => {
    if (!extModal || extModal.mode !== 'decide') return
    if (extModal.action === 'approve' && trimInvalid) {
      toast.error(`Enter between 1 and ${trimMax} day(s), or leave blank.`)
      return
    }
    setSubmitting(true)
    try {
      await adminApi.resolveAvailabilityExtension(extModal.req.id, {
        action: extModal.action,
        admin_note: extNote.trim() || undefined,
        // On approve an admin may grant FEWER days than requested.
        extra_days:
          extModal.action === 'approve' && extTrimDays ? Number(extTrimDays) : undefined,
      })
      toast.success(
        extModal.action === 'approve'
          ? 'Extension approved.'
          : extModal.action === 'reject'
            ? 'Extension rejected.'
            : 'Extension revoked.',
      )
      setExtModal(null)
      setExtNote('')
      setExtTrimDays('')
      loadExtRequests(extFilter)
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to resolve extension request.'))
    } finally {
      setSubmitting(false)
    }
  }

  // Highest value each numeric field may take. Trimming may only reduce the
  // request, and a request was itself validated against the interviewer's cap,
  // so req.extra_days is already <= MAX_EXTENSION_DAYS.
  const trimMax =
    extModal?.mode === 'decide'
      ? Math.min(extModal.req.extra_days, MAX_EXTENSION_DAYS)
      : MAX_EXTENSION_DAYS
  const trimInvalid =
    extTrimDays !== '' && (Number(extTrimDays) < 1 || Number(extTrimDays) > trimMax)

  // Everything still needing an admin action: awaiting review OR approved and
  // awaiting the cancel/reschedule itself.
  const openCrCount = changeRequests.length

  // Open requests keyed by booking, so a session row can say "there is a request
  // against this one" without a second API call.
  const crByBooking = new Map<number, AdminChangeRequestItem>()
  for (const r of changeRequests) if (!crByBooking.has(r.booking_id)) crByBooking.set(r.booking_id, r)

  // One short line, worded from what is actually in the queue. The previous copy
  // spelled out the whole approve-then-act rule and wrapped to three lines, which
  // made the explanation bigger than the table it introduced.
  const crIntro = (() => {
    const kinds = new Set(changeRequests.map((r) => r.type))
    const verb =
      kinds.size === 0
        ? 'cancel or reschedule'
        : kinds.has('cancel') && kinds.has('reschedule')
          ? 'cancel or reschedule'
          : kinds.has('cancel')
            ? 'cancel'
            : 'reschedule'
    return `Interviewers can't ${verb} their own sessions — click a row to open the session and do it there.`
  })()

  // Prefer the freshly-loaded row for the pinned request so the banner reflects
  // the approval that was just made rather than the copy captured on click.
  const focusedReq = focused
    ? changeRequests.find((r) => r.id === focused.req.id) ?? focused.req
    : null

  // The pinned session row, once loaded. A session whose time has already passed
  // cannot be cancelled or rescheduled (see canModify), so the banner must not
  // instruct an action that has no button — the admin settles the session's
  // outcome instead, which also closes the request.
  const focusedSession = focused
    ? sessions.find((s) => s.id === focused.bookingId && s.source === 'booking')
    : undefined
  const focusedActionable = focusedSession ? canModify(focusedSession) : true


  const statCards = [
    { label: 'Upcoming Sessions', value: counts.upcoming, icon: <Calendar size={24} />, iconClass: 'primary', filter: 'upcoming' as FilterTab },
    { label: 'Completed', value: counts.completed, icon: <CheckCircle size={24} />, iconClass: 'success', filter: 'completed' as FilterTab },
    { label: 'Pending Evaluations', value: counts.pending_evaluation, icon: <AlertCircle size={24} />, iconClass: 'warning', filter: 'pending_evaluation' as FilterTab },
    { label: 'Rescheduled', value: counts.rescheduled, icon: <RotateCcw size={24} />, iconClass: 'primary', filter: 'rescheduled' as FilterTab },
    { label: 'Cancelled', value: counts.cancelled, icon: <XCircle size={24} />, iconClass: 'danger', filter: 'cancelled' as FilterTab },
  ]

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title">Session Management</h1>
          {/* The cards and tab badges follow the filters now, so the subtitle has
              to say when they are describing one interviewer instead of everyone. */}
          <p className="page-subtitle">
            {countsScope
              ? `Showing sessions for ${countsScope}`
              : 'View and manage all interview sessions'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <ExportMenu
            buildPayload={buildExportPayload}
            onExport={runExport}
            defaultTimezone={timezone}
            busy={exporting}
            disabled={loading}
          />
          <button className="btn btn-secondary as-refresh-btn" onClick={refetch} disabled={loading}>
            <RefreshCcw size={18} />
            <span className="as-refresh-label">Refresh</span>
          </button>
        </div>
      </div>

      {/* Stat Cards — clickable to filter */}
      <div className="stats-grid as-stats-grid">
        {statCards.map((card) => (
          <div
            key={card.label}
            className={`stat-card${card.label === 'Cancelled' ? ' as-cancelled-card' : ''}`}
            style={{ cursor: card.filter ? 'pointer' : 'default', transition: 'box-shadow 0.15s' }}
            onClick={() => card.filter && handleTabChange(card.filter)}
          >
            <div
              className={`icon ${card.iconClass}`}
              style={card.iconClass === 'danger' ? { background: 'rgba(229,62,62,0.1)', color: 'var(--danger)' } : undefined}
            >
              {card.icon}
            </div>
            <div className="value">{card.value}</div>
            <div className="label">{card.label}</div>
          </div>
        ))}
      </div>

      {/* View toggle: Sessions vs Change Requests */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {([
          { key: 'sessions', label: 'Sessions' },
          { key: 'change_requests', label: 'Change Requests' },
          { key: 'extended_availability', label: 'Availability Extensions' },
          { key: 'manage_availability', label: 'Manage Availability' },
        ] as const).map((v) => {
          const badge =
            v.key === 'change_requests'
              ? openCrCount
              : v.key === 'extended_availability'
                ? extPendingCount
                : 0
          return (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              border: '1px solid var(--border)',
              background: view === v.key ? 'var(--primary-navy)' : 'white',
              color: view === v.key ? 'white' : 'var(--text-muted)',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {v.key !== 'sessions' && <Inbox size={16} />}
            {v.label}
            {badge > 0 && (
              <span
                style={{
                  minWidth: 20,
                  padding: '1px 7px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  background: view === v.key ? 'rgba(255,255,255,0.25)' : 'var(--danger)',
                  color: 'white',
                }}
              >
                {badge}
              </span>
            )}
          </button>
          )
        })}
      </div>

      {view === 'sessions' ? (
        <div className="card">
          {/* Toolbar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 220, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200, border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px' }}>
                <Search size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search by client or interviewer..."
                  value={search}
                  // Searching means "show me the list again", so like the status
                  // tabs it releases the single-session pin — otherwise the typed
                  // text would be silently ignored while one row stayed on screen.
                  onChange={(e) => { setFocused(null); setSearch(e.target.value) }}
                  style={{ border: 'none', boxShadow: 'none', padding: 0, flex: 1 }}
                />
              </div>
              <select
                className="form-input"
                value={interviewerFilter}
                onChange={(e) => { setInterviewerFilter(e.target.value); setPage(1) }}
                style={{ width: 'auto', minWidth: 180, maxWidth: 240 }}
                aria-label="Filter by interviewer"
              >
                <option value="">All interviewers</option>
                {interviewers.map((iv) => (
                  <option key={iv.interviewer_id} value={String(iv.interviewer_id)}>
                    {iv.alias || `Interviewer #${iv.interviewer_id}`}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {STATUS_TABS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => handleTabChange(key)}
                  style={{
                    padding: '6px 14px',
                    border: '1px solid var(--border)',
                    background: activeTab === key ? 'var(--primary-navy)' : 'white',
                    color: activeTab === key ? 'white' : 'var(--text-muted)',
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {label}
                  <span style={{ marginLeft: 6, opacity: 0.75, fontSize: 11 }}>({getTabCount(key)})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Focus banner: the list is pinned to ONE booking, opened from the
              change-request queue. It has to be obvious that the search box and
              the status tabs are not what produced this single row, and there has
              to be an unmistakable way back out. */}
          {focused && focusedReq && (
            <div
              className="as-focus-banner"
              role="status"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
                padding: '12px 16px',
                marginBottom: 16,
                background: 'rgba(49,130,206,0.08)',
                border: '1px solid rgba(49,130,206,0.35)',
                borderRadius: 8,
              }}
            >
              <div style={{ fontSize: 13, lineHeight: 1.6, minWidth: 260, flex: 1 }}>
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Inbox size={15} />
                  Showing one session — change request #{focusedReq.id}
                </div>
                <div style={{ color: 'var(--text-muted)' }}>
                  {focusedReq.interviewer_alias || 'The interviewer'} asked to{' '}
                  <strong>{focusedReq.type === 'cancel' ? 'cancel' : 'reschedule'}</strong> this session
                  {focusedReq.reason ? <> — “{focusedReq.reason}”</> : null}.
                  {!focusedActionable ? (
                    /* Past session: cancel/reschedule are gone, so pointing at
                       them would send the admin looking for a button that is not
                       there. Marking the outcome is the action that applies, and
                       it closes the request too. */
                    <>
                      {' '}This session&apos;s time has already passed, so it can no longer be
                      cancelled or rescheduled.{' '}
                      <strong>Mark its outcome below</strong> (or reject the request) to
                      close it out.
                    </>
                  ) : needsApprovalStep(focusedReq) && !isAwaitingAction(focusedReq) ? (
                    <> This request is still awaiting your decision.</>
                  ) : (
                    <>
                      {' '}
                      <strong>
                        {focusedReq.type === 'cancel'
                          ? 'Cancel the session below'
                          : 'Reschedule the session below'}
                      </strong>{' '}
                      to grant the request and clear it from the queue.
                    </>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="btn btn-secondary"
                  style={{ padding: '6px 14px', fontSize: 13, whiteSpace: 'nowrap' }}
                  onClick={() => { clearFocus(); setView('change_requests') }}
                >
                  <Inbox size={15} /> Back to requests
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ padding: '6px 14px', fontSize: 13, whiteSpace: 'nowrap' }}
                  onClick={clearFocus}
                >
                  <X size={15} /> Show all sessions
                </button>
              </div>
            </div>
          )}

          {fetchError && (
            <div style={{ padding: '10px 16px', marginBottom: 12, background: 'rgba(245,101,101,0.1)', border: '1px solid rgba(245,101,101,0.3)', borderRadius: 8, fontSize: 13, color: 'var(--danger)' }}>
              {fetchError}
            </div>
          )}

          {/* Table */}
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Interviewer</th>
                  <th>Date &amp; Time ({tzAbbr(timezone)})</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading sessions...</td>
                  </tr>
                ) : fetchError ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Unable to load sessions.</td>
                  </tr>
                ) : sessions.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                      {focused
                        ? `Session #${focused.bookingId} could not be found — it may have been replaced by a reschedule. Use "Show all sessions" to browse the list.`
                        : 'No sessions found'}
                    </td>
                  </tr>
                ) : (
                  sessions.map((session) => {
                    const displayName = session.client_name || session.client_email || 'Unknown'
                    // Only booking rows can carry a change request (requests FK to
                    // calendly_bookings.id, and the merged list shares an id space
                    // with legacy sessions — see the actions cell below).
                    const openCr = isBooking(session) ? crByBooking.get(session.id) : undefined
                    return (
                      <tr
                        key={`${session.source}-${session.id}`}
                        // Highlight the row the admin was sent here to act on.
                        style={
                          focused?.bookingId === session.id && isBooking(session)
                            ? { background: 'rgba(49,130,206,0.06)' }
                            : undefined
                        }
                      >
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="avatar">{displayName.charAt(0).toUpperCase()}</div>
                            <div>
                              <div style={{ fontWeight: 500 }}>{displayName}</div>
                              {session.client_name && session.client_email && (
                                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{session.client_email}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{session.interviewer_alias || '—'}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                            <Calendar size={12} />{formatDateInTz(session.scheduled_at, timezone)}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                            <Clock size={12} />{formatTimeInTz(session.scheduled_at, timezone)}
                          </div>
                        </td>
                        <td>{session.duration_minutes} min</td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                            <span className={`badge ${getStatusBadgeClass(session.display_status)}`}>
                              {formatStatus(session.display_status)}
                            </span>
                            {/* Makes an outstanding request visible from the Sessions
                                tab too, so an admin browsing the list can see that
                                this session is waiting on them — not only someone who
                                arrived via the queue. */}
                            {openCr && (
                              <span
                                className={`badge ${isAwaitingAction(openCr) ? 'badge-primary' : 'badge-warning'}`}
                                style={{ whiteSpace: 'normal', fontSize: 11 }}
                                title={openCr.reason || undefined}
                              >
                                {openCr.type === 'cancel' ? 'Cancellation' : 'Reschedule'}{' '}
                                {isAwaitingAction(openCr) ? 'approved — action needed' : 'requested'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ fontWeight: 500 }}>
                          {session.amount != null ? `$${session.amount.toFixed(2)}` : '—'}
                        </td>
                        <td>
                          {/* SECURITY: booking-action endpoints act on CalendlyBooking ids.
                              The merged list mixes source==="booking" and source==="session"
                              in a non-namespaced integer id space, so these actions are rendered
                              ONLY for booking rows, using session.id as the booking_id. */}
                          {isBooking(session) ? (
                            <div className="as-actions" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {canModify(session) && (
                                <>
                                  <button style={iconBtnStyle} title="Reschedule" aria-label="Reschedule" onClick={() => openModal('reschedule', session)}>
                                    <RotateCcw size={14} />
                                  </button>
                                  <button style={iconBtnStyle} title="Reassign interviewer" aria-label="Reassign interviewer" onClick={() => openModal('reassign', session)}>
                                    <UserCog size={14} />
                                  </button>
                                </>
                              )}
                              {/* No admin action for attendance failure: that is
                                  decided by Zoom attendance data + the grace job,
                                  and admins only view the resulting list. */}
                              {/* "Mark completed" is offered only while the session
                                  is still live. A cancelled or no-show row is a
                                  settled outcome and stays read-only. */}
                              {canResolveAttendance(session) && session.display_status !== 'no_show' && (
                                <button style={iconBtnStyle} title="Mark completed" aria-label="Mark completed" onClick={() => openModal('complete', session)}>
                                  <Check size={14} />
                                </button>
                              )}
                              {canModify(session) && (
                                <button style={{ ...iconBtnStyle, color: 'var(--danger)' }} title="Cancel session" aria-label="Cancel session" onClick={() => openModal('cancel', session)}>
                                  <XCircle size={14} />
                                </button>
                              )}
                              {/* Calendar file for live sessions only — see
                                  canAddToCalendar. Nothing that is cancelled, over
                                  or missed belongs in anyone's calendar. */}
                              {canAddToCalendar(session) && (
                                <button
                                  style={iconBtnStyle}
                                  title="Add to calendar (.ics)"
                                  aria-label="Add to calendar"
                                  onClick={() =>
                                    downloadIcs(
                                      `session-${session.id}.ics`,
                                      buildAdminIcs({
                                        bookingId: session.id,
                                        clientName: session.client_name,
                                        interviewerAlias: session.interviewer_alias,
                                        scheduledStartUtc: session.scheduled_at,
                                        durationMinutes: session.duration_minutes,
                                        status: session.display_status,
                                        zoomJoinUrl: session.zoom_join_url,
                                        zoomPassword: session.zoom_password,
                                        timezone,
                                        calendarUid: session.calendar_uid,
                                        calendarSequence: session.calendar_sequence,
                                      }),
                                    )
                                  }
                                >
                                  <CalendarPlus size={14} />
                                </button>
                              )}
                              <button style={iconBtnStyle} title="View history" aria-label="View history" onClick={() => openModal('history', session)}>
                                <History size={14} />
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, padding: '0 4px' }}>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{total} session{total !== 1 ? 's' : ''} total</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
                <span style={{ padding: '6px 12px', fontSize: 13, color: 'var(--text-muted)' }}>{page} / {totalPages}</span>
                <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
            </div>
          )}
        </div>
      ) : view === 'change_requests' ? (
        /* ── Change Requests review queue ──────────────────────────────────── */
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 16 }}>Interviewer Change Requests</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                {crIntro}
              </p>
            </div>
            <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={loadChangeRequests} disabled={crLoading}>
              <RefreshCcw size={16} /> Refresh
            </button>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Interviewer</th>
                  <th>Client</th>
                  <th>Session Time ({tzAbbr(timezone)})</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>Requested</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {crLoading ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading requests...</td></tr>
                ) : changeRequests.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No open change requests</td></tr>
                ) : (
                  changeRequests.map((req) => (
                    /* The whole row opens the session. It is also a real keyboard
                       target: tabbing to it and pressing Enter/Space does the same
                       thing, so the shortcut is not mouse-only. The Approve/Reject
                       buttons stopPropagation so they don't also navigate. */
                    <tr
                      key={req.id}
                      tabIndex={0}
                      role="button"
                      aria-label={`Open session for ${req.client_name || req.client_email || 'client'}`}
                      title="Open this session in the Sessions tab"
                      onClick={() => openSessionForRequest(req)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          openSessionForRequest(req)
                        }
                      }}
                      className="as-cr-row"
                    >
                      <td>{req.interviewer_alias || (req.interviewer_id != null ? `#${req.interviewer_id}` : '—')}</td>
                      <td>{req.client_name || req.client_email || '—'}</td>
                      <td>
                        {req.scheduled_start_utc ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                              <Calendar size={12} />{formatDateInTz(req.scheduled_start_utc, timezone)}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                              <Clock size={12} />{formatTimeInTz(req.scheduled_start_utc, timezone)}
                            </div>
                          </>
                        ) : '—'}
                      </td>
                      <td>
                        <span className={`badge ${req.type === 'cancel' ? 'badge-danger' : 'badge-warning'}`}>
                          {formatStatus(req.type)}
                        </span>
                      </td>
                      <td>
                        {/* An approved request is still outstanding work, so it says
                            what is left to do rather than just "Approved". */}
                        <span
                          className={`badge ${isAwaitingAction(req) ? 'badge-primary' : 'badge-warning'}`}
                          style={{ whiteSpace: 'normal' }}
                        >
                          {crStatusLabel(req)}
                        </span>
                      </td>
                      <td style={{ maxWidth: 260, whiteSpace: 'normal', fontSize: 13, color: 'var(--text-muted)' }}>
                        {req.reason || '—'}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{timeAgo(req.created_at)}</td>
                      {/* stopPropagation everywhere: these buttons sit inside a
                          row that navigates on click. */}
                      {/* Actions hold only what cannot be done by opening the
                          session. There is no "go to the session" button: the whole
                          row already does that, and duplicating it as a button just
                          added a second control for one destination. */}
                      <td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {/* Approve is offered ONLY where it decides something the
                              action itself cannot — i.e. agreeing to move a session
                              before the new time is known. A cancellation needs no
                              such step. */}
                          {needsApprovalStep(req) && !isAwaitingAction(req) && (
                            <button
                              className="btn btn-primary"
                              style={{ padding: '6px 12px', fontSize: 13 }}
                              onClick={() => { setCrModal({ req, action: 'approve' }); setCrNotes('') }}
                            >
                              <Check size={14} /> Approve
                            </button>
                          )}
                          {/* Declining stays available until a decision is recorded.
                              The server refuses a second one, so an already-approved
                              row offers none. */}
                          {!isAwaitingAction(req) ? (
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '6px 12px', fontSize: 13, color: 'var(--danger)' }}
                              onClick={() => { setCrModal({ req, action: 'reject' }); setCrNotes('') }}
                            >
                              <X size={14} /> Reject
                            </button>
                          ) : (
                            /* Already decided and nothing left but to carry it out on
                               the session. A blank cell reads as a rendering fault, so
                               say why it is empty. */
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                              Open the session
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : view === 'extended_availability' ? (
        /* ── Availability Extension review queue ───────────────────────────── */
        <div className="card">
          {/* Header: the copy is width-capped so the action buttons stay on this
              row at laptop widths instead of wrapping into a row of their own. */}
          <div className="as-ext-head">
            <div className="as-ext-head-text">
              <h3 style={{ margin: 0, fontSize: 16 }}>Availability Extension Requests</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Interviewers can request availability beyond their standard booking window (up to 60 days). Approve or reject a request — and switch to “Approved (active)” to revoke a window you already granted.
              </p>
            </div>
            <div className="as-ext-head-actions">
              <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13, whiteSpace: 'nowrap' }} onClick={() => loadExtRequests(extFilter)} disabled={extLoading}>
                <RefreshCcw size={16} /> Refresh
              </button>
            </div>
          </div>

          {/* Status filter — 'Approved (active)' is what makes an already-granted
              extension visible so it can be revoked. Shares its row with the row
              count so the table starts higher up the page. */}
          <div className="as-ext-toolbar">
            <div className="as-ext-filters">
              {([
                { key: 'pending', label: 'Pending review' },
                { key: 'approved', label: 'Approved (active)' },
                { key: 'all', label: 'All' },
              ] as const).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { setExtFilter(key); loadExtRequests(key) }}
                  disabled={extLoading}
                  className={extFilter === key ? 'btn btn-primary' : 'btn btn-secondary'}
                  style={{ padding: '5px 12px', fontSize: 12.5, whiteSpace: 'nowrap' }}
                >
                  {label}
                  {key === 'pending' && extPendingCount > 0 ? ` (${extPendingCount})` : ''}
                </button>
              ))}
            </div>
            {!extLoading && extRequests.length > 0 && (
              <span className="as-ext-count">
                {extRequests.length} request{extRequests.length === 1 ? '' : 's'}
              </span>
            )}
          </div>

          <div className="table-container">
            <table className="as-ext-table">
              {/* Fixed proportions: without these the six short columns spread
                  evenly and leave dead space between them on a wide laptop. */}
              <colgroup>
                <col style={{ width: 160 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 96 }} />
                <col />{/* Reason takes the leftover width */}
                <col style={{ width: 86 }} />
                {/* Wide enough for Approve + Reject side by side (they need
                    ~192px of content box, plus this cell's padding) */}
                <col style={{ width: 232 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Interviewer</th>
                  <th>Extra Days</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>Requested</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {extLoading ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading requests...</td></tr>
                ) : extRequests.length === 0 ? (
                  <tr className="as-ext-empty-row"><td colSpan={6} className="as-ext-empty">
                    <CalendarPlus size={28} style={{ color: 'var(--border)' }} />
                    <div style={{ fontWeight: 600, color: 'var(--text-dark)', fontSize: 14 }}>
                      {extFilter === 'pending'
                        ? 'No pending extension requests'
                        : extFilter === 'approved'
                          ? 'No active extensions to revoke'
                          : 'No extension requests yet'}
                    </div>
                    <div style={{ fontSize: 13 }}>
                      {extFilter === 'pending'
                        ? 'Requests from interviewers land here for review.'
                        : extFilter === 'approved'
                          ? 'Windows you grant will appear here so you can revoke them.'
                          : 'Extension requests raised by interviewers will appear here.'}
                    </div>
                  </td></tr>
                ) : (
                  extRequests.map((req) => (
                    <tr key={req.id}>
                      <td
                        style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={req.interviewer_alias || `#${req.interviewer_id}`}
                      >
                        {req.interviewer_alias || `#${req.interviewer_id}`}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, whiteSpace: 'nowrap' }}>
                          <Calendar size={12} style={{ flexShrink: 0 }} />
                          <strong>+{req.extra_days}</strong>&nbsp;day{req.extra_days === 1 ? '' : 's'}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${req.status === 'pending' ? 'badge-warning' : req.status === 'approved' ? 'badge-success' : 'badge-danger'}`}>
                          {formatStatus(req.status)}
                        </span>
                      </td>
                      <td className="as-ext-reason" title={req.reason || undefined}>
                        <span>{req.reason || '—'}</span>
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{timeAgo(req.created_at)}</td>
                      <td>
                        {/* Actions mirror the server guard in
                            admin_decide_extension: approve/reject act only on a
                            pending row, revoke on pending OR approved. Anything
                            already decided has no action left. */}
                        <div className="as-ext-row-actions">
                          {req.status === 'pending' && (
                            <>
                              <button
                                className="btn btn-primary"
                                style={{ padding: '6px 12px', fontSize: 13, whiteSpace: 'nowrap' }}
                                onClick={() => { setExtModal({ mode: 'decide', req, action: 'approve' }); setExtNote(''); setExtTrimDays('') }}
                              >
                                <Check size={14} /> Approve
                              </button>
                              <button
                                className="btn btn-secondary"
                                style={{ padding: '6px 12px', fontSize: 13, color: 'var(--danger)', whiteSpace: 'nowrap' }}
                                onClick={() => { setExtModal({ mode: 'decide', req, action: 'reject' }); setExtNote('') }}
                              >
                                <X size={14} /> Reject
                              </button>
                            </>
                          )}
                          {/* Revoke is offered ONLY on an approved row, where it is the
                              "cancel a window I already granted" action. On a pending row it
                              duplicated Reject — same terminal outcome, two buttons — so it
                              is gone from there. */}
                          {req.status === 'approved' && (
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '6px 12px', fontSize: 13, color: 'var(--danger)', whiteSpace: 'nowrap' }}
                              onClick={() => { setExtModal({ mode: 'decide', req, action: 'revoke' }); setExtNote('') }}
                            >
                              <Ban size={14} /> Revoke Extension
                            </button>
                          )}
                          {req.status !== 'pending' && req.status !== 'approved' && (
                            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                              No action available
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* ── Manage Availability (feature #9): edit one interviewer's scheduler ─ */
        <AdminManageAvailability interviewers={interviewers} />
      )}

      {/* ── Reschedule modal ─────────────────────────────────────────────────── */}
      {modal?.kind === 'reschedule' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3 className="modal-title">Reschedule Session</h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">New date</label>
                <input
                  type="date"
                  className="form-input"
                  value={slotDate}
                  min={todayStr}
                  onChange={(e) => {
                    const d = e.target.value
                    setSlotDate(d)
                    if (modal.session.interviewer_id != null) loadSlots(modal.session.interviewer_id, d)
                  }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Available slots ({tzAbbr(timezone)})</label>
                {renderSlotList()}
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  Only the interviewer’s open availability can be selected.
                </p>
              </div>
              <div className="form-group">
                <label className="form-label">Reason <span style={{ color: 'var(--danger)' }}>*</span> (required)</label>
                <textarea className="form-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this being rescheduled?" />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={submitting}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleReschedule} disabled={submitting || !selectedSlotUtc || !reason.trim()}>
                {submitting ? 'Saving...' : 'Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reassign modal ───────────────────────────────────────────────────── */}
      {modal?.kind === 'reassign' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3 className="modal-title">Reassign Interviewer</h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">New interviewer</label>
                <select
                  className="form-input"
                  value={reassignId}
                  onChange={(e) => {
                    const v = e.target.value
                    setReassignId(v)
                    setSelectedSlotUtc('')
                    if (v) loadSlots(Number(v), slotDate)
                    else { setSlots([]); setSlotsError('') }
                  }}
                >
                  <option value="">Select an interviewer...</option>
                  {interviewers
                    .filter((iv) => iv.interviewer_id !== modal.session.interviewer_id)
                    .map((iv) => (
                      <option key={iv.interviewer_id} value={String(iv.interviewer_id)}>
                        {iv.alias || `Interviewer #${iv.interviewer_id}`}
                      </option>
                    ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={slotDate}
                  min={todayStr}
                  disabled={!reassignId}
                  onChange={(e) => {
                    const d = e.target.value
                    setSlotDate(d)
                    if (reassignId) loadSlots(Number(reassignId), d)
                  }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Available slots ({tzAbbr(timezone)})</label>
                {reassignId
                  ? renderSlotList()
                  : <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>Select an interviewer to see their available slots.</p>}
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  The new interviewer must have an open slot — pick from their availability.
                </p>
              </div>
              <div className="form-group">
                <label className="form-label">Reason <span style={{ color: 'var(--danger)' }}>*</span> (required)</label>
                <textarea className="form-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={submitting}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleReassign} disabled={submitting || !reassignId || !selectedSlotUtc || !reason.trim()}>
                {submitting ? 'Saving...' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel modal ─────────────────────────────────────────────────────── */}
      {modal?.kind === 'cancel' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h3 className="modal-title">Cancel Session</h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                Cancel the session with <strong>{modal.session.client_name || modal.session.client_email || 'this client'}</strong>
                {modal.session.interviewer_alias ? <> and <strong>{modal.session.interviewer_alias}</strong></> : null}? Both parties will be notified.
              </p>
              {/* When the cancel was reached from the queue, restate what is being
                  honoured — and that this click is what closes the request. The
                  admin should never have to remember what they clicked through
                  from in order to write the reason. */}
              {crByBooking.get(modal.session.id) && isBooking(modal.session) && (
                <div
                  style={{
                    marginTop: 12,
                    padding: '10px 12px',
                    background: 'rgba(49,130,206,0.08)',
                    border: '1px solid rgba(49,130,206,0.3)',
                    borderRadius: 8,
                    fontSize: 13,
                    lineHeight: 1.6,
                  }}
                >
                  {(() => {
                    const cr = crByBooking.get(modal.session.id)!
                    return (
                      <>
                        <strong>Change request #{cr.id}</strong> —{' '}
                        {cr.interviewer_alias || 'the interviewer'} asked to{' '}
                        {cr.type === 'cancel' ? 'cancel' : 'reschedule'} this session
                        {cr.reason ? <> (“{cr.reason}”)</> : null}. Cancelling now marks that
                        request resolved and removes it from the Change Requests tab.
                      </>
                    )
                  })()}
                </div>
              )}
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Reason <span style={{ color: 'var(--danger)' }}>*</span> (required)</label>
                <textarea className="form-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this being cancelled?" />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={submitting}>Keep Session</button>
              <button
                type="button"
                className="btn"
                style={{ background: 'var(--danger)', color: '#fff', border: 'none' }}
                onClick={handleCancel}
                disabled={submitting || !reason.trim()}
              >
                {submitting ? 'Cancelling...' : 'Cancel Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mark completed confirm modal ─────────────────────────────────────── */}
      {modal?.kind === 'complete' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3 className="modal-title">Mark as Completed</h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                Mark the session with <strong>{modal.session.client_name || modal.session.client_email || 'this client'}</strong> as{' '}
                <strong>completed</strong>?
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={submitting}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleStatus('completed')}
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── History modal ────────────────────────────────────────────────────── */}
      {modal?.kind === 'history' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h3 className="modal-title">Reschedule History</h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="modal-body">
              {historyLoading ? (
                <p style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>Loading history...</p>
              ) : history.length === 0 ? (
                <p style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No reschedule history for this booking.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {history.map((h) => {
                    const interviewerChanged =
                      h.previous_interviewer_id != null &&
                      h.new_interviewer_id != null &&
                      h.previous_interviewer_id !== h.new_interviewer_id
                    return (
                      <div key={h.sequence_no} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>#{h.sequence_no}</span>
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            {formatDateInTz(h.created_at, timezone)} {formatTimeInTz(h.created_at, timezone)}
                            {h.rescheduled_by_role ? ` · by ${h.rescheduled_by_role}` : ''}
                          </span>
                        </div>
                        <div style={{ fontSize: 13 }}>
                          <span style={{ color: 'var(--text-muted)' }}>
                            {formatDateInTz(h.previous_start_utc, timezone)} {formatTimeInTz(h.previous_start_utc, timezone)}
                          </span>
                          {' → '}
                          <span style={{ fontWeight: 500 }}>
                            {formatDateInTz(h.new_start_utc, timezone)} {formatTimeInTz(h.new_start_utc, timezone)}
                          </span>
                        </div>
                        {interviewerChanged && (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                            Interviewer changed: #{h.previous_interviewer_id} → #{h.new_interviewer_id}
                          </div>
                        )}
                        {h.reason && (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Reason: {h.reason}</div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Change-request resolve modal ─────────────────────────────────────── */}
      {crModal && (
        <div className="modal-overlay" onClick={() => !submitting && setCrModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3 className="modal-title">
                {crModal.action === 'approve' ? 'Approve' : 'Reject'} {formatStatus(crModal.req.type)} Request
              </h3>
              <button onClick={() => !submitting && setCrModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="modal-body">
              {/* Approve is a DECISION, not the action — say so plainly. It used to
                  cancel the booking on the spot, which made the most destructive
                  action in the console a side effect of a button labelled "Approve".
                  Only reschedules reach this branch now. */}
              <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                {crModal.action === 'approve' && (
                  <>
                    Approving records your decision. It does <strong>not</strong> move the
                    session — nothing is sent to the client or the interviewer yet.
                    You&apos;ll be taken to the session to pick the new time, and doing that
                    is what completes the request and clears it from this queue.
                  </>
                )}
                {crModal.action === 'reject' && (
                  <>Rejecting will leave the booking unchanged and notify the interviewer.</>
                )}
              </p>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Admin notes (optional)</label>
                <textarea className="form-input" rows={3} value={crNotes} onChange={(e) => setCrNotes(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setCrModal(null)} disabled={submitting}>Cancel</button>
              <button
                type="button"
                className="btn"
                style={crModal.action === 'reject'
                  ? { background: 'var(--danger)', color: '#fff', border: 'none' }
                  : undefined}
                onClick={handleResolve}
                disabled={submitting}
              >
                {submitting ? 'Saving...' : crModal.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Availability extension resolve modal ────────────────────────────── */}
      {extModal && (
        <div className="modal-overlay" onClick={() => !submitting && setExtModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3 className="modal-title">
                {`${extModal.action === 'approve' ? 'Approve' : extModal.action === 'reject' ? 'Reject' : 'Revoke'} Extension`}
              </h3>
              <button onClick={() => !submitting && setExtModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                {extModal.action === 'approve' && (
                  <>Approving adds <strong>{extModal.req.extra_days} day(s)</strong> to this
                  interviewer&apos;s standard booking window from now on (rolling, capped at 60
                  days total) and notifies them.</>
                )}
                {extModal.action === 'reject' && (
                  <>Rejecting leaves the standard window in place and notifies the interviewer.</>
                )}
                {extModal.action === 'revoke' && (
                  <>Revoking removes the extra days and returns this interviewer to their
                  standard booking window, then notifies them.</>
                )}
              </p>
              {extModal.action === 'approve' && (
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="form-label">Grant fewer days (optional)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={extTrimDays}
                    min={1}
                    max={trimMax}
                    step={1}
                    onChange={(e) => setExtTrimDays(e.target.value)}
                  />
                  {trimInvalid ? (
                    <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>
                      Enter between 1 and {trimMax} day(s), or leave blank.
                    </p>
                  ) : (
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                      Leave blank to grant the {extModal.req.extra_days} day(s) requested.
                      You can only reduce it — {MAX_EXTENSION_DAYS} days is the maximum
                      extension.
                    </p>
                  )}
                </div>
              )}
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Admin note (optional)</label>
                <textarea className="form-input" rows={3} value={extNote} onChange={(e) => setExtNote(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setExtModal(null)} disabled={submitting}>Cancel</button>
              <button
                type="button"
                className="btn"
                style={extModal.mode === 'decide' && extModal.action !== 'approve'
                  ? { background: 'var(--danger)', color: '#fff', border: 'none' }
                  : { background: 'var(--primary-navy)', color: '#fff', border: 'none' }}
                onClick={handleResolveExt}
                disabled={
                  submitting ||
                  (extModal.mode === 'decide' && extModal.action === 'approve' && trimInvalid)
                }
              >
                {submitting
                  ? 'Saving...'
                  : extModal.action === 'approve'
                    ? 'Approve'
                    : extModal.action === 'reject'
                      ? 'Reject'
                      : 'Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Stat cards: desktop 5-col → tablet 3-col → mobile always 2-col */
        .as-stats-grid { grid-template-columns: repeat(5, 1fr) !important; }
        @media (max-width: 1280px) { .as-stats-grid { grid-template-columns: repeat(3, 1fr) !important; } }
        @media (max-width: 768px)  { .as-stats-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        /* Mobile: keep 2-col; Cancelled spans full width as its own row */
        @media (max-width: 640px) {
          .as-stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
          .as-cancelled-card { grid-column: 1 / -1; display: flex !important; flex-direction: row !important; align-items: center !important; gap: 16px; }
          .as-cancelled-card .icon { margin-bottom: 0 !important; flex-shrink: 0; }
          .as-cancelled-card .value { font-size: 24px; margin-bottom: 0; }
          .as-cancelled-card .label { margin-top: 2px; }
        }
        @media (max-width: 480px) {
          .as-stats-grid { gap: 8px !important; }
          .as-stats-grid .stat-card { padding: 14px 12px !important; }
          .as-stats-grid .icon { width: 38px !important; height: 38px !important; }
          .as-stats-grid .value { font-size: 22px !important; }
        }

        /* Refresh/Export buttons: icon-only on small screens */
        @media (max-width: 480px) {
          .as-refresh-btn { padding: 8px 10px !important; }
          .as-refresh-label { display: none; }
        }

        .as-actions button:hover { border-color: var(--primary-navy); color: var(--primary-navy); }

        /* ── Change-request rows are navigation ─────────────────────────────
           A whole row opens the session it refers to, so it has to LOOK
           clickable — a bare <tr role="button"> gives no affordance at all — and
           it must show a focus ring for keyboard users, since the row is a real
           tab stop. */
        .as-cr-row { cursor: pointer; transition: background 0.12s; }
        .as-cr-row:hover { background: rgba(49, 130, 206, 0.07); }
        .as-cr-row:focus-visible {
          outline: 2px solid var(--primary-navy);
          outline-offset: -2px;
        }

        /* The focus banner stacks its buttons under the copy on narrow screens
           rather than squeezing them into an unreadable column. */
        @media (max-width: 640px) {
          .as-focus-banner > div:last-child { width: 100%; }
          .as-focus-banner .btn { flex: 1 1 auto; justify-content: center; }
        }

        /* ── Availability Extensions panel ──────────────────────────────────
           The header copy used to run the full width of the card, which pushed
           the action buttons onto their own row and the filters onto a third —
           three stacked control rows before the table on a laptop. Capping the
           copy keeps Refresh inline and the table above the fold. */
        .as-ext-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px 20px;
          margin-bottom: 14px;
        }
        .as-ext-head-text { flex: 1 1 380px; min-width: 0; max-width: 68ch; }
        .as-ext-head-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          flex-shrink: 0;
          margin-left: auto;
        }

        /* Filters share a row with the result count */
        .as-ext-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px 12px;
          margin-bottom: 12px;
        }
        .as-ext-filters { display: flex; gap: 8px; flex-wrap: wrap; min-width: 0; }
        .as-ext-count { font-size: 12.5px; color: var(--text-muted); white-space: nowrap; }

        /* Columns are proportioned by <colgroup>; this keeps the table from
           collapsing them once the viewport is narrow — it scrolls instead. */
        /* min-width = the fixed columns + a readable Reason. Laptops at 1280+
           show the whole table; below that .table-container scrolls it, which
           is how every other table on the page behaves. */
        .as-ext-table { min-width: 820px; table-layout: fixed; }
        /* Slightly denser than the default 16px so six columns read as one
           group instead of drifting apart */
        .as-ext-table th, .as-ext-table td { padding: 14px 12px; }
        .as-ext-table th:first-child, .as-ext-table td:first-child { padding-left: 16px; }
        .as-ext-table th:last-child, .as-ext-table td:last-child { padding-right: 16px; }
        .as-ext-table td { vertical-align: middle; }
        /* A long reason clamps to two lines (full text on hover) so one verbose
           request can't make its row three times taller than the rest */
        .as-ext-reason { font-size: 13px; color: var(--text-muted); }
        .as-ext-reason > span {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          white-space: normal;
        }
        .as-ext-row-actions {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .as-ext-table th:last-child { text-align: right; }

        .as-ext-empty {
          text-align: center;
          padding: 44px 20px !important;
          color: var(--text-muted);
        }
        .as-ext-empty > * { margin: 0 auto 6px; }
        .as-ext-empty > *:last-child { margin-bottom: 0; }
        /* No row-hover tint on the placeholder — it read as a selected row */
        .as-ext-empty-row:hover { background: transparent; }

        @media (max-width: 900px) {
          .as-ext-head-text { max-width: none; }
          .as-ext-head-actions { margin-left: 0; }
        }
        @media (max-width: 560px) {
          .as-ext-head-actions { width: 100%; }
          .as-ext-head-actions .btn { flex: 1 1 auto; justify-content: center; }
          /* Filters scroll sideways rather than stacking three rows tall */
          .as-ext-filters {
            flex-wrap: nowrap;
            overflow-x: auto;
            scrollbar-width: none;
            padding-bottom: 2px;
          }
          .as-ext-filters::-webkit-scrollbar { display: none; }
          .as-ext-filters .btn { flex-shrink: 0; }
        }
      `}</style>
    </div>
  )
}
